import { createElement, useEffect, useId, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { useCopyEdit } from "../context/CopyEditContext";
import { useSiteCopy } from "../context/SiteCopyContext";
import { getAtPath } from "../lib/siteCopyPaths";

type IntrinsicText =
  | "span"
  | "p"
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "em"
  | "cite"
  | "strong"
  | "i";

export function CopyText({
  path,
  as = "span",
  className,
  style,
}: {
  path: string;
  as?: IntrinsicText;
  className?: string;
  style?: CSSProperties;
}) {
  const { copy, savePath } = useSiteCopy();
  const { isAdmin } = useAuth();
  const { activePath, openEdit, closeEdit } = useCopyEdit();
  const titleId = useId();
  const raw = getAtPath(copy, path);
  const text = raw == null ? "" : String(raw);
  const open = activePath === path;
  const editBlocked = activePath !== null && activePath !== path;
  const [draft, setDraft] = useState(text);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setDraft(text);
  }, [text, open]);

  function handleClose() {
    closeEdit(path);
  }

  async function onSave() {
    setSaving(true);
    setErr(null);
    try {
      await savePath(path, draft);
      handleClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const modal =
    open &&
    createPortal(
      <div
        className="copy-text-modal-backdrop"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget && !saving) handleClose();
        }}
      >
        <div
          className="copy-text-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id={titleId} style={{ margin: "0 0 12px", fontSize: 16 }}>
            Edit text
          </h3>
          <p style={{ margin: "0 0 8px", fontSize: 12, opacity: 0.75, wordBreak: "break-all" }}>
            {path}
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontFamily: "inherit",
              fontSize: 14,
              padding: 10,
              borderRadius: 8,
              border: "1px solid rgba(6,11,30,0.2)",
            }}
          />
          {err && <p style={{ color: "#a40000", fontSize: 13, marginTop: 8 }}>{err}</p>}
          <div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={handleClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void onSave()}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <span className="copy-text-root" style={{ position: "relative", display: "inline" }}>
        {createElement(as, { className, style }, text)}
        {isAdmin && (
          <button
            type="button"
            className={`copy-text-edit${editBlocked ? " copy-text-edit--blocked" : ""}`}
            aria-label={`Edit copy: ${path}`}
            title="Edit (admin)"
            disabled={editBlocked}
            onClick={() => {
              setDraft(text);
              openEdit(path);
            }}
          >
            ✎
          </button>
        )}
      </span>
      {modal}
    </>
  );
}
